// src/sentry/config.ts
import * as Sentry from "@sentry/react";
var IGNORED_ERRORS = [
  // Browser extensions
  "top.GLOBALS",
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "atomicFindClose",
  // Network errors that are user-caused
  "Non-Error promise rejection captured",
  // ResizeObserver errors (benign)
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications"
];
function initSentryClient(config) {
  const env = {
    dsn: config.dsn,
    environment: config.environment || "development",
    appVersion: config.appVersion || "1.0.0",
    deploymentType: config.deploymentType || config.defaultDeploymentType || "app"
  };
  if (typeof window === "undefined" || !env.dsn) {
    return Sentry;
  }
  Sentry.init({
    dsn: env.dsn,
    environment: env.environment,
    release: `${config.appName}@${env.appVersion}`,
    // Deployment context
    initialScope: {
      tags: {
        deploymentType: env.deploymentType,
        platform: "browser"
      }
    },
    // Trace sampling
    tracesSampleRate: env.environment === "production" ? 0.1 : 1,
    // Session replay sampling
    replaysSessionSampleRate: 0.1,
    // 10% of sessions
    replaysOnErrorSampleRate: 1,
    // 100% of sessions with errors
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration({
        enableLongTask: true,
        enableInp: true
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      }),
      Sentry.feedbackIntegration({
        autoInject: false
      }),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true
      })
    ],
    // Ignore common non-critical errors
    ignoreErrors: IGNORED_ERRORS,
    // Before send hook - sanitize and filter
    beforeSend(event) {
      if (env.environment === "development") {
        console.log("Sentry event (dev mode - not sent):", event);
        return null;
      }
      if (event.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        const isExtension = frames.some(
          (frame) => frame.filename?.includes("chrome-extension://") || frame.filename?.includes("moz-extension://")
        );
        if (isExtension) {
          return null;
        }
      }
      if (event.request?.headers) {
        delete event.request.headers["Authorization"];
        delete event.request.headers["Cookie"];
      }
      if (event.request?.cookies) {
        event.request.cookies = {};
      }
      return event;
    },
    // Before breadcrumb - sanitize
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (breadcrumb.data?.url) {
          const url = new URL(breadcrumb.data.url, window.location.origin);
          url.searchParams.delete("token");
          url.searchParams.delete("apiKey");
          url.searchParams.delete("api_key");
          breadcrumb.data.url = url.toString();
        }
        if (breadcrumb.data?.["request.headers"]) {
          delete breadcrumb.data["request.headers"]["Authorization"];
          delete breadcrumb.data["request.headers"]["Cookie"];
        }
      }
      if (breadcrumb.category === "console" && env.environment === "production") {
        if (breadcrumb.level !== "error" && breadcrumb.level !== "warning") {
          return null;
        }
      }
      return breadcrumb;
    },
    // Enable debug mode in development
    debug: env.environment === "development"
  });
  Sentry.setContext("app", {
    deploymentType: env.deploymentType,
    version: env.appVersion
  });
  Sentry.setContext("browser", {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor
  });
  return Sentry;
}
export {
  IGNORED_ERRORS,
  Sentry,
  initSentryClient
};
